// The Aleva MCP registry review (milestone 16): admins see submitted servers
// and approve or reject them. Approved servers reach the public registry on
// the next export. Crawl-discovered servers are already approved.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, can } from '../api'
import { ErrorBox, Pill } from '../components'
import { dt } from '../format'

const statuses = ['verified', 'pending', 'approved', 'rejected', '']

export function MCPServers() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const admin = can(me.data, 'admin')
  const [status, setStatus] = useState('verified')
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['mcp-servers', status], queryFn: () => api.mcpServers(status) })
  const act = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) => (approve ? api.approveMCPServer(id) : api.rejectMCPServer(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-servers'] }),
  })

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>MCP registry</h1>
          <p className="sub">Swedish MCP servers: crawl-discovered (auto-approved) and community submissions awaiting review. Approved servers join the public registry on the next export.</p>
        </div>
      </header>
      <div className="dash-filters">
        <label className="dash-filter">
          <span className="muted">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => (
              <option key={s} value={s}>{s || 'all'}</option>
            ))}
          </select>
        </label>
      </div>
      {q.error && <ErrorBox error={q.error} />}
      {act.error && <ErrorBox error={act.error} />}
      <table className="tbl">
        <thead>
          <tr>
            <th>Server</th>
            <th>Source</th>
            <th>Status</th>
            <th>Submitted</th>
            {admin && <th>Review</th>}
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((s) => (
            <tr key={s.id}>
              <td>
                <div>{s.name || s.domain}</div>
                <a href={s.url} className="mono muted" target="_blank" rel="noreferrer">{s.url}</a>
                {s.description && <div className="muted">{s.description}</div>}
              </td>
              <td><Pill kind={s.source === 'crawl' ? 'info' : 'neutral'}>{s.source}</Pill></td>
              <td><Pill kind={s.status === 'approved' ? 'ok' : s.status === 'rejected' ? 'err' : 'neutral'}>{s.status}</Pill></td>
              <td className="muted">{dt(s.created_at)}</td>
              {admin && (
                <td>
                  {(s.status === 'verified' || s.status === 'pending' || s.status === 'rejected') && (
                    <button disabled={act.isPending} onClick={() => act.mutate({ id: s.id, approve: true })}>Approve</button>
                  )}
                  {s.status !== 'rejected' && (
                    <button disabled={act.isPending} onClick={() => act.mutate({ id: s.id, approve: false })}>Reject</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!q.isLoading && q.data?.length === 0 && <p className="muted">No servers with this status.</p>}
    </section>
  )
}
